<template>
  <section class="portfolio-access">
    <div class="panel issue-panel">
      <div class="panel-heading">
        <div>
          <h2>열람 토큰 발급</h2>
          <p>발급한 토큰은 설정한 시간 동안만 포트폴리오 열람에 사용할 수 있습니다.</p>
        </div>
      </div>

      <form class="issue-form" @submit.prevent="createToken">
        <label class="field hours-field">
          <span>유효 시간 <b>*</b></span>
          <div class="input-suffix">
            <input
              v-model.number="form.validHours"
              type="number"
              min="1"
              max="168"
              step="1"
              required
            />
            <em>시간</em>
          </div>
          <small>1~168시간 (최대 7일)</small>
        </label>

        <label class="field label-field">
          <span>발급 목적 <b>*</b></span>
          <input
            v-model.trim="form.label"
            type="text"
            maxlength="100"
            placeholder="예: 큐브 분양 전 테스트"
            required
          />
          <small>{{ form.label.length }}/100자</small>
        </label>

        <button class="primary-button" type="submit" :disabled="creating || !form.label.trim()">
          {{ creating ? "발급 중..." : "토큰 발급" }}
        </button>
      </form>

      <div v-if="issuedToken" class="issued-card" aria-live="polite">
        <div class="issued-title">
          <strong>토큰이 발급되었습니다.</strong>
          <span>{{ issuedToken.expiresAt }}까지 유효</span>
        </div>
        <div class="token-row">
          <code>{{ issuedToken.token }}</code>
          <button type="button" class="secondary-button" @click="copyIssuedValue">
            {{ copyLabel }}
          </button>
        </div>
        <p v-if="shareUrl" class="share-url">{{ shareUrl }}</p>
        <p v-else class="config-help">
          공유 링크가 필요하면 <code>VUE_APP_PORTFOLIO_URL</code> 환경변수에 포트폴리오 주소를 설정하세요.
        </p>
      </div>
    </div>

    <div class="panel list-panel">
      <div class="panel-heading list-heading">
        <div>
          <h2>현재 유효 토큰</h2>
          <p>만료 임박 순으로 표시됩니다. 만료된 토큰은 목록에서 자동으로 사라집니다.</p>
        </div>
        <div class="heading-actions">
          <button type="button" class="secondary-button" :disabled="loading" @click="loadTokens">
            {{ loading ? "조회 중..." : "새로고침" }}
          </button>
          <button
            type="button"
            class="danger-button"
            :disabled="loading || tokens.length === 0"
            @click="expireAll"
          >
            전체 만료
          </button>
        </div>
      </div>

      <div v-if="loading && tokens.length === 0" class="empty-state">토큰을 불러오는 중입니다.</div>
      <div v-else-if="tokens.length === 0" class="empty-state">현재 열람 가능한 토큰이 없습니다.</div>
      <div v-else class="table-scroll">
        <table>
          <thead>
            <tr>
              <th>전달 대상</th>
              <th>토큰</th>
              <th>발급자</th>
              <th>발급 일시</th>
              <th>만료 일시</th>
              <th>남은 시간</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="item in tokens" :key="item.token">
              <td>{{ item.label || "-" }}</td>
              <td><code>{{ item.token }}</code></td>
              <td>{{ item.issuedBy || "-" }}</td>
              <td>{{ item.issuedAt }}</td>
              <td>{{ item.expiresAt }}</td>
              <td><span class="remaining">{{ formatRemaining(item.remainSeconds) }}</span></td>
              <td>
                <button type="button" class="row-danger-button" @click="expireOne(item)">즉시 만료</button>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  </section>
</template>

<script>
import { removeAuthData } from "@/libs/auth-helper";
import {
  createPortfolioToken,
  expireAllPortfolioTokens,
  expirePortfolioToken,
  getPortfolioTokenList
} from "@/api/portfolio";

export default {
  name: "PortfolioAccess",
  data() {
    return {
      form: {
        validHours: 24,
        label: ""
      },
      tokens: [],
      issuedToken: null,
      creating: false,
      loading: false,
      copyLabel: "토큰 복사"
    };
  },
  computed: {
    shareUrl() {
      const baseUrl = (process.env.VUE_APP_PORTFOLIO_URL || "").trim();
      if (!baseUrl || !this.issuedToken) return "";

      try {
        const url = new URL(baseUrl);
        url.searchParams.set("t", this.issuedToken.token);
        return url.toString();
      } catch (error) {
        return "";
      }
    }
  },
  created() {
    this.loadTokens();
  },
  methods: {
    showResultError(result) {
      alert(this.resultErrorMessage(result));
      if (result && (result.resultCode === "TK999" || result.resultCode === "1000")) {
        removeAuthData();
        this.$store.commit("clearAllSiteData");
        // Reload to stop the old session's timers and socket connections.
        window.location.replace(this.$router.resolve({ name: "login" }).href);
      }
    },
    resultErrorMessage(result) {
      if (!result) return "서버 응답을 확인할 수 없습니다.";
      if (result.resultCode === "TK999") return "다른 곳에서 로그인되었습니다. 다시 로그인해주세요.";
      if (result.resultCode === "1000") return "인증이 만료되었거나 접근 권한이 없습니다.";
      return result.resultMessage || "요청을 처리하지 못했습니다.";
    },
    validateForm() {
      const hours = this.form.validHours;
      if (!Number.isInteger(hours) || hours < 1 || hours > 168) {
        alert("유효 시간은 1~168 사이의 정수로 입력해주세요.");
        return false;
      }
      if (!this.form.label.trim()) {
        alert("발급 목적을 입력해주세요.");
        return false;
      }
      if (this.form.label.length > 100) {
        alert("발급 목적은 100자 이내로 입력해주세요.");
        return false;
      }
      return true;
    },
    async createToken() {
      if (!this.validateForm() || this.creating) return;

      this.creating = true;
      this.copyLabel = "토큰 복사";
      try {
        const result = await createPortfolioToken({
          validHours: this.form.validHours,
          label: this.form.label
        });
        if (result.resultCode !== "0") {
          this.showResultError(result);
          return;
        }
        this.issuedToken = result.data;
        this.form.label = "";
        await this.loadTokens();
      } catch (error) {
        alert("토큰 발급 중 오류가 발생했습니다.");
      } finally {
        this.creating = false;
      }
    },
    async loadTokens() {
      if (this.loading) return;
      this.loading = true;
      try {
        const result = await getPortfolioTokenList();
        if (result.resultCode !== "0") {
          this.showResultError(result);
          return;
        }
        this.tokens = result.data && Array.isArray(result.data.list) ? result.data.list : [];
      } catch (error) {
        alert("토큰 목록을 불러오지 못했습니다.");
      } finally {
        this.loading = false;
      }
    },
    async expireOne(item) {
      if (!confirm(`'${item.label || item.token}' 토큰을 즉시 만료하시겠습니까?`)) return;
      try {
        const result = await expirePortfolioToken(item.token);
        if (result.resultCode !== "0") {
          this.showResultError(result);
          return;
        }
        await this.loadTokens();
      } catch (error) {
        alert("토큰 만료 처리 중 오류가 발생했습니다.");
      }
    },
    async expireAll() {
      if (!confirm(`현재 유효한 토큰 ${this.tokens.length}개를 모두 즉시 만료하시겠습니까?`)) return;
      try {
        const result = await expireAllPortfolioTokens();
        if (result.resultCode !== "0") {
          this.showResultError(result);
          return;
        }
        this.tokens = [];
        alert(`${result.data.expiredCount}개 토큰을 만료했습니다.`);
      } catch (error) {
        alert("전체 만료 처리 중 오류가 발생했습니다.");
      }
    },
    async copyIssuedValue() {
      const value = this.shareUrl || this.issuedToken.token;
      try {
        await navigator.clipboard.writeText(value);
        this.copyLabel = this.shareUrl ? "링크 복사됨" : "토큰 복사됨";
      } catch (error) {
        const textarea = document.createElement("textarea");
        textarea.value = value;
        textarea.style.position = "fixed";
        textarea.style.opacity = "0";
        document.body.appendChild(textarea);
        textarea.select();
        document.execCommand("copy");
        document.body.removeChild(textarea);
        this.copyLabel = this.shareUrl ? "링크 복사됨" : "토큰 복사됨";
      }
    },
    formatRemaining(seconds) {
      const total = Math.max(0, Number(seconds) || 0);
      const days = Math.floor(total / 86400);
      const hours = Math.floor((total % 86400) / 3600);
      const minutes = Math.floor((total % 3600) / 60);
      if (days > 0) return `${days}일 ${hours}시간`;
      if (hours > 0) return `${hours}시간 ${minutes}분`;
      return `${minutes}분`;
    }
  }
};
</script>

<style scoped>
.portfolio-access {
  display: grid;
  gap: 20px;
  color: #27273f;
}
.panel {
  padding: 24px;
  border: 1px solid #e6e6f6;
  border-radius: 8px;
  background: #fff;
  box-shadow: 0 3px 12px rgba(39, 39, 63, 0.05);
}
.panel-heading {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 20px;
  margin-bottom: 22px;
}
.panel-heading h2 {
  margin: 0 0 7px;
  font-size: 18px;
  font-weight: 800;
}
.panel-heading p {
  margin: 0;
  color: #77778f;
  font-size: 13px;
}
.issue-form {
  display: grid;
  grid-template-columns: 180px minmax(260px, 1fr) 120px;
  align-items: end;
  gap: 16px;
}
.field {
  display: flex;
  flex-direction: column;
  gap: 7px;
}
.field > span {
  font-size: 13px;
  font-weight: 700;
}
.field b {
  color: #e5484d;
}
.field input {
  box-sizing: border-box;
  width: 100%;
  height: 38px;
  padding: 0 12px;
  border: 1px solid #d8d8e8;
  border-radius: 4px;
  outline: none;
}
.field input:focus {
  border-color: #126ae2;
  box-shadow: 0 0 0 3px rgba(18, 106, 226, 0.1);
}
.field small {
  min-height: 14px;
  color: #8f8faa;
  font-size: 11px;
}
.input-suffix {
  position: relative;
}
.input-suffix input {
  padding-right: 45px;
}
.input-suffix em {
  position: absolute;
  top: 50%;
  right: 12px;
  color: #77778f;
  font-size: 12px;
  font-style: normal;
  transform: translateY(-50%);
}
button {
  border: 0;
  border-radius: 4px;
  cursor: pointer;
  font-weight: 700;
}
button:disabled {
  cursor: not-allowed;
  opacity: 0.55;
}
.primary-button {
  height: 38px;
  margin-bottom: 21px;
  background: #126ae2;
  color: #fff;
}
.secondary-button,
.danger-button {
  height: 34px;
  padding: 0 14px;
  border: 1px solid #d8d8e8;
  background: #fff;
  color: #46465f;
}
.danger-button,
.row-danger-button {
  border-color: #f1b9bb;
  color: #c62f35;
}
.issued-card {
  margin-top: 22px;
  padding: 18px;
  border: 1px solid #b9d5fb;
  border-radius: 6px;
  background: #f4f8ff;
}
.issued-title {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 12px;
}
.issued-title strong {
  color: #0c4695;
}
.issued-title span {
  color: #696981;
  font-size: 12px;
}
.token-row {
  display: flex;
  align-items: center;
  gap: 10px;
}
.token-row code {
  flex: 1;
  padding: 11px 13px;
  border: 1px dashed #9bbdeb;
  border-radius: 4px;
  background: #fff;
  color: #143b6f;
  font-size: 17px;
  font-weight: 800;
  letter-spacing: 1px;
}
.share-url,
.config-help {
  margin: 10px 0 0;
  overflow-wrap: anywhere;
  color: #696981;
  font-size: 12px;
}
.heading-actions {
  display: flex;
  gap: 8px;
}
.empty-state {
  padding: 50px 20px;
  border-top: 1px solid #ededf5;
  color: #8f8faa;
  text-align: center;
}
.table-scroll {
  overflow-x: auto;
}
table {
  width: 100%;
  border-collapse: collapse;
  font-size: 12px;
}
th,
td {
  padding: 12px 10px;
  border-bottom: 1px solid #ededf5;
  text-align: left;
  white-space: nowrap;
}
th {
  background: #f7f7fb;
  color: #696981;
  font-weight: 700;
}
td code {
  color: #143b6f;
  font-weight: 700;
  letter-spacing: 0.4px;
}
.remaining {
  color: #0c4695;
  font-weight: 700;
}
.row-danger-button {
  padding: 6px 9px;
  border: 1px solid;
  background: #fff;
  font-size: 11px;
}
@media (max-width: 900px) {
  .issue-form {
    grid-template-columns: 1fr;
  }
  .primary-button {
    margin-bottom: 0;
  }
  .panel-heading,
  .issued-title {
    align-items: stretch;
    flex-direction: column;
  }
}
</style>
